import type React from 'react';
import type { FC } from 'react';

import { useTranslation } from '@crehana/i18n';
import CloseSmallOutline from '@crehana/icons/CloseSmallOutline';
import IconButton from '@crehana/ui/dist/Buttons/IconButton';
import PrimaryButton from '@crehana/ui/dist/Buttons/PrimaryButton';
import TextLink from '@crehana/ui/dist/Buttons/TextLink';
import { baseLighter } from '@crehana/ui/dist/styles/colors';
import { useUITheme } from '@crehana/web';
import { clsx } from 'clsx';

import localeEs from '@/views/AdminDashboard/i18n/localeEs';

import type { MyCustomHelperProps } from './types';

const MyCustomHelper: FC<MyCustomHelperProps> = ({
  current,
  content,
  totalSteps,
  gotoStep,
  close,
  onClose,
  isSecondTour = false,
}) => {
  const { t } = useTranslation();
  const { isDark } = useUITheme();
  const pagination = `${current + 1} / ${totalSteps}`;
  const hasPrev = current > 0;
  const endStep = current + 1 === totalSteps;

  return (
    <div
      className={clsx(
        'relative rounded-5',
        isDark ? 'bg-base-dark' : 'bg-white shadow-1-dp',
      )}
    >
      {onClose && (
        <button
          type="button"
          className="absolute right-0 mr-24 mt-12"
          onClick={onClose}
        >
          <CloseSmallOutline size={24} color={baseLighter} />
        </button>
      )}
      {content as React.ReactNode}
      <div className="flex items-center  mt-32 px-24 pb-12 justify-between">
        {hasPrev && !isSecondTour && (
          <IconButton
            onClick={() => gotoStep(current - 1)}
            disabled={current === 0}
            arrow="left"
            customSize={16}
            style={{ width: 32, height: 32 }}
            isDark={isDark}
          />
        )}

        {isSecondTour && (
          <TextLink
            type="button"
            label={t('TOUR_BUTTON_AGAIN', {
              defaultValue: localeEs.es.TOUR_BUTTON_AGAIN,
            })}
            onClick={() => gotoStep(0)}
            isDark={isDark}
          />
        )}

        {!isSecondTour && (
          <span className="font-label text-gray-dark">{pagination}</span>
        )}

        <PrimaryButton
          onClick={() => {
            if (endStep) {
              close();
            } else {
              gotoStep(current + 1);
            }
          }}
          isDark={isDark}
          label={
            isSecondTour
              ? t('TOUR_BUTTON_START', {
                  defaultValue: localeEs.es.TOUR_BUTTON_START,
                })
              : t('TOUR_BUTTON_NEXT', {
                  defaultValue: localeEs.es.TOUR_BUTTON_NEXT,
                })
          }
          size="xs"
        />
      </div>
    </div>
  );
};

export default MyCustomHelper;
